import type { ReactNode } from 'react';
import type { StorefrontSection, StorefrontSectionType } from '@mlm/shared';
import { HeroSection } from './sections/hero';
import { FlashSaleSection } from './sections/flash-sale';
import { FeaturedPackagesSection } from './sections/featured-packages';
import { CategoryGridSection } from './sections/category-grid';
import { ProductFeedSection } from './sections/product-feed';
import { BannerGroupSection } from './sections/banner-group';
import { SpacerSection } from './sections/spacer';
import { RichLandingSection } from './sections/rich-landing';
import { ValuesGridSection } from './sections/values-grid';
import { HowItWorksSection } from './sections/how-it-works';
import { PackagesSection } from './sections/packages';
import { TestimonialsSection } from './sections/testimonials';

type SectionComponent = (props: { section: StorefrontSection }) => ReactNode;

/**
 * type → component. PARTIAL by design: only the storefront-block sections that
 * have a renderer today are listed. Types in the union without a renderer yet
 * (productRow, cmsArticle, and the Phase-3 landing/marketplace-chrome types)
 * render nothing (with a dev warning) — an unrecognized section is forward-
 * compatible, never a crash. Add a renderer here when the type is first used.
 */
const SECTION_REGISTRY: Partial<Record<StorefrontSectionType, SectionComponent>> = {
  hero: HeroSection,
  flashSale: FlashSaleSection,
  featuredPackages: FeaturedPackagesSection,
  categoryGrid: CategoryGridSection,
  productFeed: ProductFeedSection,
  bannerGroup: BannerGroupSection,
  spacer: SpacerSection,
  // Landing blocks (corporate / landing-first tenants)
  richLanding: RichLandingSection,
  valuesGrid: ValuesGridSection,
  howItWorks: HowItWorksSection,
  packages: PackagesSection,
  testimonials: TestimonialsSection,
};

/**
 * Renders an ordered section list to its components (enabled only), keyed by id.
 * No wrapper element — sections own their `<section>` + spacing — so a layout
 * that mirrors the baseline order renders byte-identically to the old page.
 */
export function SectionRenderer({ sections }: { sections: StorefrontSection[] }) {
  return (
    <>
      {sections
        .filter((s) => s.enabled)
        .map((s) => {
          const C = SECTION_REGISTRY[s.type];
          if (!C) {
            if (import.meta.env.DEV) {
              console.warn(`[storefront] no renderer for section type "${s.type}"`);
            }
            return null;
          }
          return <C key={s.id} section={s} />;
        })}
    </>
  );
}
